"use client";

import React, { useState } from "react";
import Banner from "@/components/sections/Banner";
import {
  FaEnvelope,
  FaFacebookF,
  FaInstagram,
  FaWhatsapp,
  FaArrowLeft,
  FaFolder,
  FaFolderOpen
} from "react-icons/fa";
import { FaXTwitter } from "react-icons/fa6";
import { LuDownload } from "react-icons/lu";
import { IoDocumentTextOutline } from "react-icons/io5";

// Explorer Node Structure
interface ExplorerNode {
  id: string;
  name: string;
  type: "folder" | "file";
  fileType?: "docx" | "pptx" | "pdf" | "xlsx" | "csv" | "xls" | "ppt";
  filePath?: string;
  previewPath?: string;
  children?: ExplorerNode[];
}

// PDF Reference Document Mock Content
const pdfMockData: Record<string, { title: string; subtitle: string; paragraphs: string[] }> = {
  "m1-ferc1": {
    title: "FERC Order 2222: Distributed Energy Resources Summary",
    subtitle: "Federal Energy Regulatory Commission Guidance",
    paragraphs: [
      "FERC Order 2222 allows distributed energy resources (DERs) to aggregate and participate directly in wholesale energy markets. By removing key market barriers, this landmark regulation treats small-scale batteries, solar arrays, and electric vehicles on par with traditional generation resources.",
      "Key takeaways include guidelines for regional transmission organizations (RTOs) to establish rules governing DER aggregators, operational coordination protocols between transmission and distribution utilities, and measurement/verification rules to prevent double-counting of service values.",
    ],
  },
  "m1-ferc2": {
    title: "UK OFGEM: Local Flexibility Tenders & Pricing Guide",
    subtitle: "UK Office of Gas and Electricity Markets Regulation",
    paragraphs: [
      "UK's regulatory authority, OFGEM, mandates distribution network operators (DNOs) to run flexibility tenders before committing capital to traditional grid reinforcement schemes.",
      "This framework establishes transparent local pricing for active power scheduling, peak demand curtailment, and reactive power control. Aggregators bid their flex capabilities, and grid operators deploy them during network contingency conditions.",
    ],
  },
  "m1-dr1": {
    title: "US Demand Response Case Studies",
    subtitle: "International DR-DSM Best Practices",
    paragraphs: [
      "Case studies from PJM, California ISO (CAISO), and ERCOT demonstrate how smart load curtailment mitigates extreme weather grid spikes.",
      "Industrial load shedding, commercial smart-thermostat orchestrations, and residential vehicle-to-grid (V2G) discharging have successfully deferred billions of dollars in peak generation investments.",
    ],
  },
  "m1-neso1": {
    title: "UK National Grid ESO: Grid Code Amendments",
    subtitle: "NESO Demand Flexibility Service Regulations",
    paragraphs: [
      "NESO's Grid Code amendments outline operational guidelines for dispatching demand flexibility services.",
      "These codes mandate baseline calculation methodologies, standard pricing caps, and settlement timelines to streamline aggregator participation in winter grid-balancing actions.",
    ],
  },
  "m2-smart1": {
    title: "Smart Metering Specifications in India",
    subtitle: "National Smart Grid Mission (NSGM) Guidelines",
    paragraphs: [
      "The Government of India has launched a massive smart meter rollout program under the Revamped Distribution Sector Scheme (RDSS).",
      "Technical specifications outline requirements for hourly or 15-minute interval logging, remote disconnect/connect switches, dynamic pricing download capacities, and communication security standards (IS 16444).",
    ],
  },
  "m3-model1": {
    title: "Load Curve Simulation & Forecasting Guide",
    subtitle: "Utility Demand Flexibility Modelling Practices",
    paragraphs: [
      "Modelling demand response relies on simulating consumer response curves to price changes.",
      "This guide introduces mathematical methodologies to calculate short-run elasticity of demand, model bottom-up load profiles of household appliances, and compute aggregated peak clipping volumes under ToD tariffs.",
    ],
  },
  "m4-cba1": {
    title: "TRC and RIM Testing Guidelines",
    subtitle: "California Standard Practice Manual for Cost-Benefit Analysis",
    paragraphs: [
      "Evaluating demand-side programs requires calculating cost-benefit ratios using standard tests.",
      "The Total Resource Cost (TRC) test measures net costs/benefits to the utility and participants combined. The Ratepayer Impact Measure (RIM) test evaluates potential utility tariff impacts for non-participating customers.",
    ],
  },
};

// High-fidelity structured content for the 4 modules
const modulesDetailedData: Record<number, {
  title: string;
  gradient: string;
  manualPages: { header: string; heading?: string; paragraphs: string[] }[];
  slides: { slideNum: number; title: string; subtitle?: string; date?: string; bullets?: string[] }[];
}> = {
  1: {
    title: "Principles of DF and DSM in India and International Experiences",
    gradient: "linear-gradient(135deg, #11375c 0%, #1c5270 100%)",
    manualPages: [
      {
        header: "Training Manual: Demand Flexibility and DSM in India",
        heading: "Principles of Demand Flexibility & Demand-Side Management",
        paragraphs: [
          "Demand Flexibility (DF) and Demand-Side Management (DSM) represent critical pathways for enhancing power system reliability and facilitating variable renewable energy (VRE) integration. This training manual provides the foundational principles underlying modern demand-side interventions.",
          "In the traditional power system paradigm, supply dynamically followed load fluctuations. However, with rising shares of intermittent wind and solar resources, grid management requires demand to become flexible and active. By dynamically shifting, shedding, or modulating loads, utilities can mitigate peak demand spikes, decrease thermal generation reliance, and minimize grid congestion.",
        ],
      },
      {
        header: "Training Manual: Demand Flexibility and DSM in India",
        heading: "1. Variable Renewable Energy (VRE) Integration Challenges",
        paragraphs: [
          "Electricity distribution companies (DISCOMs) in India face unique structural and scheduling challenges. Intermittent solar output during daylight hours creates steep ramping requirements during the evening peak—a phenomenon widely referred to as the 'duck curve.'",
          "Without flexible demand, DISCOMs are forced to purchase expensive power from the spot exchange (IEX) to balance supply shortfalls. This increases overall power purchase costs, which are ultimately passed down to consumers. Deploying demand flexibility reduces reliance on costly peaking plants and stabilizes grid frequency.",
        ],
      },
      {
        header: "Training Manual: Demand Flexibility and DSM in India",
        heading: "2. International Best Practices & Benchmarking",
        paragraphs: [
          "Several global jurisdictions have successfully integrated demand-side resources into wholesale electricity markets. In the United States, FERC Order 2222 permits distributed energy resource (DER) aggregators to participate directly alongside traditional generators.",
          "Similarly, in the United Kingdom, the Office of Gas and Electricity Markets (OFGEM) has established regulatory frameworks that incentivize system operators to utilize demand response and local flexibility tenders. Benchmarking these international frameworks helps tailor appropriate regulatory mechanisms for India's specific utility context.",
        ],
      },
    ],
    slides: [
      {
        slideNum: 1,
        title: "Module-1 Principles of DF and DSM in India and International Experiences and Regulatory",
        subtitle: "Sustainable Energy for All (SEforALL) | MP Ensystems Advisory Pvt Ltd",
        date: "29 June 2026",
      },
      {
        slideNum: 2,
        title: "Contents",
        bullets: [
          "RE integration impact on DISCOMs, IEX prices, demand, power purchase cost",
          "DF and DSM concepts - DF strategies, international case studies, Indian case studies",
          "DF/DSM regulations in India - Timeline, objectives",
          "International DF regulations, flexibility market structure",
        ],
      },
      {
        slideNum: 3,
        title: "1. Introduction to RE integration impact on DISCOMs, IEX prices, demand, power purchase cost",
        subtitle: "Section Overview",
      },
      {
        slideNum: 4,
        title: "Introduction – Renewables in the Global Energy System",
        bullets: [
          "Global energy demand continued to rise in 2023, requiring immediate grid actions.",
          "Total final energy consumption grew by 2.2% across all end-use sectors.",
          "Renewables represented an estimated 13.5% of total final energy consumption, requiring greater grid flexibility.",
        ],
      },
      {
        slideNum: 5,
        title: "Renewable Integration and DISCOM Impacts",
        bullets: [
          "Variable RE integration impacts grid frequency, voltage stability, and day-ahead scheduling.",
          "DISCOMs face increased power purchase costs due to balancing and backup power needs.",
          "Peak demand alignment with solar generation is a key challenge for state utilities.",
        ],
      },
    ],
  },
  2: {
    title: "DF Roll-out Enablers",
    gradient: "linear-gradient(135deg, #0e5b69 0%, #0c3945 100%)",
    manualPages: [
      {
        header: "Training Manual: Demand Flexibility and DSM in India",
        heading: "DF Roll-out Enablers: Technological, Regulatory, and Institutional",
        paragraphs: [
          "Rolling out Demand Flexibility programs successfully requires a tripartite alignment of technology, regulation, and institutional capabilities. This section details the key enablers that utilities must establish.",
          "Smart metering infrastructure, dynamic tariff structures, and robust DISCOM billing systems represent the essential building blocks for load-modifying programs.",
        ],
      },
      {
        header: "Training Manual: Demand Flexibility and DSM in India",
        heading: "1. Advanced Metering Infrastructure (AMI)",
        paragraphs: [
          "Advanced Metering Infrastructure (AMI) provides the digital foundation for demand flexibility. Without interval meters capable of recording consumption at 15-minute or hourly resolutions, operators cannot verify load reductions.",
          "AMI enables two-way communication, allowing utilities to send pricing signals or dispatch commands directly to smart appliances or commercial building management systems (BMS).",
        ],
      },
    ],
    slides: [
      {
        slideNum: 1,
        title: "Module-2 DF Roll-out Enablers",
        subtitle: "Technology, Regulation & Institutional Structures",
        date: "29 June 2026",
      },
      {
        slideNum: 2,
        title: "Core Enablers of Demand Flexibility",
        bullets: [
          "Smart Metering Infrastructure (AMI) and Advanced Analytics",
          "Dynamic Tariff Mechanisms (ToD/ToU and Critical Peak Pricing)",
          "Institutional capacity building of DISCOM operations",
          "Aggregator business frameworks for residential/commercial loads",
        ],
      },
      {
        slideNum: 3,
        title: "Advanced Metering Infrastructure (AMI) Role",
        bullets: [
          "Provides interval load data essential for demand response validation.",
          "Enables two-way communication between grid operators and end-users.",
          "Underpins Time-of-Day (ToD) tariff execution and automated billing.",
        ],
      },
    ],
  },
  3: {
    title: "DF Modelling",
    gradient: "linear-gradient(135deg, #245241 0%, #133026 100%)",
    manualPages: [
      {
        header: "Training Manual: Demand Flexibility and DSM in India",
        heading: "Demand Flexibility Modelling and Quantitative Load Analysis",
        paragraphs: [
          "Modelling demand flexibility requires quantitative approaches to analyze electricity load curves. By estimating price elasticity and consumer responsiveness, planners can forecast how much load can be shifted or shed during critical peak hours.",
          "This section covers bottom-up end-use models, price-responsiveness frameworks, and peak-shaving calculations across industrial, commercial, and residential sectors.",
        ],
      },
    ],
    slides: [
      {
        slideNum: 1,
        title: "Module-3 DF Modelling",
        subtitle: "Quantitative Forecasting & Load Curve Analysis",
        date: "29 June 2026",
      },
      {
        slideNum: 2,
        title: "Modelling Methodologies",
        bullets: [
          "Bottom-up end-use modelling for major load categories",
          "Elasticity of demand forecasting based on dynamic pricing",
          "Price-responsiveness simulation tools",
          "Peak clipping, valley filling, and load shifting calculations",
        ],
      },
    ],
  },
  4: {
    title: "DF Program Designs, Cost-Benefit Tests and Measurements",
    gradient: "linear-gradient(135deg, #51542f 0%, #29301b 100%)",
    manualPages: [
      {
        header: "Training Manual: Demand Flexibility and DSM in India",
        heading: "DF Program Designs, Cost-Benefit Tests & Measurement Metrics",
        paragraphs: [
          "Designing implementable demand flexibility programs requires balancing grid benefit calculations with participant incentive structures. This module introduces the standard economic cost-benefit tests used by utility regulators.",
          "Furthermore, it establishes standard Measurement and Verification (M&V) protocols to accurately quantify demand reductions relative to computed baselines.",
        ],
      },
    ],
    slides: [
      {
        slideNum: 1,
        title: "Module-4 DF Program Designs, Cost-Benefit Tests & Measurements",
        subtitle: "Implementation, Economics & Measurement",
        date: "29 June 2026",
      },
      {
        slideNum: 2,
        title: "Regulatory Cost-Benefit Frameworks",
        bullets: [
          "Participant Cost Test (PCT) - Consumer economics",
          "Utility Cost Test (UCT) / Program Administrator Cost Test",
          "Ratepayer Impact Measure (RIM) - Utility tariff impacts",
          "Total Resource Cost (TRC) - Combined society/participant view",
        ],
      },
    ],
  },
};

const encodeFilePath = (path: string | undefined): string | undefined => {
  if (!path) return undefined;
  if (path.startsWith("http") || path.startsWith("blob:") || path.startsWith("data:")) return path;
  return path.split("/").map(segment => encodeURIComponent(segment)).join("/");
};

const triggerFileDownload = (filePath: string, filename: string) => {
  if (filePath) {
    const link = document.createElement("a");
    link.href = encodeFilePath(filePath) || filePath;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    return;
  }

  let mimeType = "application/octet-stream";
  let mockContent = `Mock content for file: ${filename}\nGenerated by the Mission Efficiency Demand Flexibility Training Platform.`;

  if (filename.endsWith(".docx")) {
    mimeType = "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
  } else if (filename.endsWith(".pptx")) {
    mimeType = "application/vnd.openxmlformats-officedocument.presentationml.presentation";
  } else if (filename.endsWith(".pdf")) {
    mimeType = "application/pdf";
    mockContent = `%PDF-1.4\n1 0 obj\n<< /Type /Catalog /Pages 2 0 R >>\nendobj\n2 0 obj\n<< /Type /Pages /Kids [3 0 R] /Count 1 >>\nendobj\n3 0 obj\n<< /Type /Page /Parent 2 0 R /MediaBox [0 0 612 792] /Contents 4 0 R >>\nendobj\n4 0 obj\n<< /Length 50 >>\nstream\nBT /F1 12 Tf 72 712 Td (Mock PDF content for: ${filename}) Tj ET\nendstream\nendobj\nxref\n0 5\n0000000000 65535 f\n0000000009 00000 n\n0000000056 00000 n\n0000000111 00000 n\n0000000212 00000 n\ntrailer\n<< /Size 5 >>\nstartxref\n312\n%%EOF`;
  } else if (filename.endsWith(".zip")) {
    mimeType = "application/zip";
    mockContent = `PK\x03\x04\n\x00\x00\x00\x00\x00\x00\x00\x00\x00\x00\x00\x00\x00\x00\x00\x00\x00\x00\x00\x00\x00\x00\x00\x00\x00[Mock ZIP archive content for: ${filename}]`;
  }

  const blob = new Blob([mockContent], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

// Main list data for home grid
const modulesDataList = [
  {
    id: 1,
    moduleNum: "Module 1",
    fileTypes: "DOCX - PPTX",
    title: "Principles of DF and DSM in India and International Experiences",
    description:
      "Foundational concepts of Demand Flexibility and Demand-Side Management, benchmarked against International utility practice and India's regulatory context.",
    gradient: "linear-gradient(135deg, #11375c 0%, #1c5270 100%)",
  },
  {
    id: 2,
    moduleNum: "Module 2",
    fileTypes: "DOCX - PPTX",
    title: "DF Roll-out Enablers",
    description:
      "The technological, regulatory, and institutional enablers required to roll out Demand Flexibility programs at scale across distribution utilities.",
    gradient: "linear-gradient(135deg, #0e5b69 0%, #0c3945 100%)",
  },
  {
    id: 3,
    moduleNum: "Module 3",
    fileTypes: "DOCX - PPTX",
    title: "DF Modelling",
    description:
      "Quantitative approaches to modelling demand flexibility potential, load curves, and flexibility forecasting for planning and tariff design.",
    gradient: "linear-gradient(135deg, #245241 0%, #133026 100%)",
  },
  {
    id: 4,
    moduleNum: "Module 4",
    fileTypes: "DOCX - PPTX",
    title: "DF Program Designs, Cost-Benefit Tests and Measurements",
    description:
      "Designing implementable DF programs, applying standard cost-benefit test frameworks, and measuring & verifying program impact post-deployment.",
    gradient: "linear-gradient(135deg, #51542f 0%, #29301b 100%)",
  },
];

// Nested Reference Material / File tree database (The 4 module cards act as root folders)
const fileTreeData: ExplorerNode[] = [
  {
    id: "m1",
    name: "Module 1: Principles of DF & DSM",
    type: "folder",
    children: [
      {
        id: "m1-pptx",
        name: "Module-I_Manual Training Course.pptx",
        type: "file",
        fileType: "pptx",
        filePath: "/resources/demand-flexibility-training-model/module-1/Module-I_Manual Training Course.pptx",
        previewPath: "/resources/demand-flexibility-training-model/module-1/Module-I_Manual Training Course.pdf"
      },
      {
        id: "m1-docx",
        name: "Manual-Training certification course_Module-I.docx",
        type: "file",
        fileType: "docx",
        filePath: "/resources/demand-flexibility-training-model/module-1/Manual-Training certification course_Module-I.docx",
        previewPath: "/resources/demand-flexibility-training-model/module-1/Manual-Training certification course_Module-I.pdf"
      },
      {
        id: "m1-ref1",
        name: "FERC & OFGEM regulations",
        type: "folder",
        children: [
          {
            id: "m1-ferc1",
            name: "FERC Order 2222 Summary.pdf",
            type: "file",
            fileType: "pdf",
            filePath: "/resources/demand-flexibility-training-model/module-1/Reference Material/FERC & OFGEM regulations/FERCAS~1.PDF"
          },
          {
            id: "m1-ferc2",
            name: "OFGEM Flexibility Tenders Guide.pdf",
            type: "file",
            fileType: "pdf",
            filePath: "/resources/demand-flexibility-training-model/module-1/Reference Material/FERC & OFGEM regulations/OFGEM_~1.PDF"
          },
          {
            id: "m1-ferc3",
            name: "OFGEM Consumer Survey.pdf",
            type: "file",
            fileType: "pdf",
            filePath: "/resources/demand-flexibility-training-model/module-1/Reference Material/FERC & OFGEM regulations/OFGEM_~2.PDF"
          },
          {
            id: "m1-ferc4",
            name: "Demand Response Regulations.pdf",
            type: "file",
            fileType: "pdf",
            filePath: "/resources/demand-flexibility-training-model/module-1/Reference Material/FERC & OFGEM regulations/DEMAND~1.PDF"
          }
        ]
      },
      {
        id: "m1-ref2",
        name: "International DR-DSM",
        type: "folder",
        children: [
          {
            id: "m1-dr1",
            name: "US Demand Response Case Studies.pdf",
            type: "file",
            fileType: "pdf",
            filePath: "/resources/demand-flexibility-training-model/module-1/Reference Material/International DR-DSM/DEMAND~1.PDF"
          },
          {
            id: "m1-dr2",
            name: "Intelligent Grid Integration.pdf",
            type: "file",
            fileType: "pdf",
            filePath: "/resources/demand-flexibility-training-model/module-1/Reference Material/International DR-DSM/INTELL~1.PDF"
          },
          {
            id: "m1-dr3",
            name: "DR Impact Analysis.pdf",
            type: "file",
            fileType: "pdf",
            filePath: "/resources/demand-flexibility-training-model/module-1/Reference Material/International DR-DSM/IMPACT~1.PDF"
          },
          {
            id: "m1-dr4",
            name: "Grid Congestion Analysis.pdf",
            type: "file",
            fileType: "pdf",
            filePath: "/resources/demand-flexibility-training-model/module-1/Reference Material/International DR-DSM/ANALYS~1.PDF"
          },
          {
            id: "m1-dr5",
            name: "Selected Utility DR Programs.pdf",
            type: "file",
            fileType: "pdf",
            filePath: "/resources/demand-flexibility-training-model/module-1/Reference Material/International DR-DSM/SELECT~1.PDF"
          },
          {
            id: "m1-dr6",
            name: "Flexibility Market Structures.pdf",
            type: "file",
            fileType: "pdf",
            filePath: "/resources/demand-flexibility-training-model/module-1/Reference Material/International DR-DSM/FLEXIB~1.PDF"
          },
          {
            id: "m1-dr7",
            name: "Pluggable DER Aggregation.pdf",
            type: "file",
            fileType: "pdf",
            filePath: "/resources/demand-flexibility-training-model/module-1/Reference Material/International DR-DSM/PLUGGE~1.PDF"
          },
          {
            id: "m1-dr8",
            name: "Time of Use Tariffs Study.pdf",
            type: "file",
            fileType: "pdf",
            filePath: "/resources/demand-flexibility-training-model/module-1/Reference Material/International DR-DSM/TIMEOF~1.PDF"
          },
          {
            id: "m1-dr9",
            name: "Harnessing Distributed Power.pdf",
            type: "file",
            fileType: "pdf",
            filePath: "/resources/demand-flexibility-training-model/module-1/Reference Material/International DR-DSM/HARNES~1.PDF"
          }
        ]
      },
      {
        id: "m1-ref3",
        name: "NESO DF Regulations",
        type: "folder",
        children: [
          {
            id: "m1-neso1",
            name: "UK Grid Code Amendments.pdf",
            type: "file",
            fileType: "pdf",
            filePath: "/resources/demand-flexibility-training-model/module-1/Reference Material/NESO DF Regulations/DEMAND~1.PDF"
          },
          {
            id: "m1-neso2",
            name: "Grid Stability Regulations.pdf",
            type: "file",
            fileType: "pdf",
            filePath: "/resources/demand-flexibility-training-model/module-1/Reference Material/NESO DF Regulations/DEMAND~2.PDF"
          },
          {
            id: "m1-neso3",
            name: "DFSP Participation Guidelines.pdf",
            type: "file",
            fileType: "pdf",
            filePath: "/resources/demand-flexibility-training-model/module-1/Reference Material/NESO DF Regulations/DFSPAR~1.PDF"
          }
        ]
      },
      {
        id: "m1-ref4",
        name: "Others",
        type: "folder",
        children: [
          {
            id: "m1-oth1",
            name: "BEE Cost Effectiveness Study.pdf",
            type: "file",
            fileType: "pdf",
            filePath: "/resources/demand-flexibility-training-model/module-1/Reference Material/Others/BEE_CO~1.PDF"
          },
          {
            id: "m1-oth2",
            name: "GSR 20 Regulations.pdf",
            type: "file",
            fileType: "pdf",
            filePath: "/resources/demand-flexibility-training-model/module-1/Reference Material/Others/GSR_20~1.PDF"
          },
          {
            id: "m1-oth3",
            name: "Increasing Grid Flexibility.pdf",
            type: "file",
            fileType: "pdf",
            filePath: "/resources/demand-flexibility-training-model/module-1/Reference Material/Others/INCREA~1.PDF"
          },
          {
            id: "m1-oth4",
            name: "MERC Demand-Side Management.pdf",
            type: "file",
            fileType: "pdf",
            filePath: "/resources/demand-flexibility-training-model/module-1/Reference Material/Others/MERC-D~1.PDF"
          },
          {
            id: "m1-oth5",
            name: "MERC DF Presentation.ppt",
            type: "file",
            fileType: "ppt",
            filePath: "/resources/demand-flexibility-training-model/module-1/Reference Material/Others/MERCDF~1.PPT"
          },
          {
            id: "m1-oth6",
            name: "Grid Performance Report.pdf",
            type: "file",
            fileType: "pdf",
            filePath: "/resources/demand-flexibility-training-model/module-1/Reference Material/Others/REPORT~1.PDF"
          },
          {
            id: "m1-oth7",
            name: "World Bank DSM Report.pdf",
            type: "file",
            fileType: "pdf",
            filePath: "/resources/demand-flexibility-training-model/module-1/Reference Material/Others/WORLDB~1.PDF"
          }
        ]
      }
    ]
  },
  {
    id: "m2",
    name: "Module 2: DF Roll-out Enablers",
    type: "folder",
    children: [
      {
        id: "m2-pptx1",
        name: "Introduction to Load Research Implementation.pptx",
        type: "file",
        fileType: "pptx",
        filePath: "/resources/demand-flexibility-training-model/module-2/Introduction to Load Research Implementation.pptx",
        previewPath: "/resources/demand-flexibility-training-model/module-2/Introduction to Load Research Implementation.pdf"
      },
      {
        id: "m2-pptx2",
        name: "Load Research Exercise - Delhi.pptx",
        type: "file",
        fileType: "pptx",
        filePath: "/resources/demand-flexibility-training-model/module-2/Load Research Excercise_DFDSM_Training_Delhi.pptx",
        previewPath: "/resources/demand-flexibility-training-model/module-2/Load Research Excercise_DFDSM_Training_Delhi.pdf"
      },
      {
        id: "m2-docx",
        name: "Manual-Training certification course_Module-II.docx",
        type: "file",
        fileType: "docx",
        filePath: "/resources/demand-flexibility-training-model/module-2/Manual-Training certification course_Module-II.docx",
        previewPath: "/resources/demand-flexibility-training-model/module-2/Manual-Training certification course_Module-II.pdf"
      },
      {
        id: "m2-ref1",
        name: "Consumer Survey",
        type: "folder",
        children: [
          {
            id: "m2-cs1",
            name: "Consumer Survey 2021 Engagement Report (OFGEM).pdf",
            type: "file",
            fileType: "pdf",
            filePath: "/resources/demand-flexibility-training-model/module-2/Reference Material/Consumer Survey/Consumer Survey 2021-Engagement report_OFGEM.pdf"
          },
          {
            id: "m2-cs2",
            name: "National Transmission Needs Study (USDOE).pdf",
            type: "file",
            fileType: "pdf",
            filePath: "/resources/demand-flexibility-training-model/module-2/Reference Material/Consumer Survey/National Transmission Needs Study_USDOE.pdf"
          },
          {
            id: "m2-cs3",
            name: "Capturing Grid Value.pdf",
            type: "file",
            fileType: "pdf",
            filePath: "/resources/demand-flexibility-training-model/module-2/Reference Material/Consumer Survey/CAPTUR~1.PDF"
          },
          {
            id: "m2-cs4",
            name: "Falling Battery Storage Costs.pdf",
            type: "file",
            fileType: "pdf",
            filePath: "/resources/demand-flexibility-training-model/module-2/Reference Material/Consumer Survey/FALLIN~1.PDF"
          },
          {
            id: "m2-cs5",
            name: "Lebanon Grid Assessment.pdf",
            type: "file",
            fileType: "pdf",
            filePath: "/resources/demand-flexibility-training-model/module-2/Reference Material/Consumer Survey/LEBANO~1.PDF"
          },
          {
            id: "m2-cs6",
            name: "Residential Load Shifting.pdf",
            type: "file",
            fileType: "pdf",
            filePath: "/resources/demand-flexibility-training-model/module-2/Reference Material/Consumer Survey/RESIDE~1.PDF"
          }
        ]
      },
      {
        id: "m2-ref2",
        name: "Load Research",
        type: "folder",
        children: [
          {
            id: "m2-lr1",
            name: "NERC Load Modeling Composition.pdf",
            type: "file",
            fileType: "pdf",
            filePath: "/resources/demand-flexibility-training-model/module-2/Reference Material/Load Research/Load modeling composition_NERC.pdf"
          },
          {
            id: "m2-lr2",
            name: "Load Research Best Practices.pdf",
            type: "file",
            fileType: "pdf",
            filePath: "/resources/demand-flexibility-training-model/module-2/Reference Material/Load Research/BESTPR~1.PDF"
          }
        ]
      },
      {
        id: "m2-ref3",
        name: "Resource Adequacy",
        type: "folder",
        children: [
          {
            id: "m2-ra1",
            name: "CERC Resource Adequacy Regulations 2023.pdf",
            type: "file",
            fileType: "pdf",
            filePath: "/resources/demand-flexibility-training-model/module-2/Reference Material/Resource Adequacy/CERC Resource Adequacy Regulations-2023.pdf"
          },
          {
            id: "m2-ra2",
            name: "EEE Resource Adequacy study.pdf",
            type: "file",
            fileType: "pdf",
            filePath: "/resources/demand-flexibility-training-model/module-2/Reference Material/Resource Adequacy/EEE_RE~1.PDF"
          }
        ]
      },
      {
        id: "m2-ref4",
        name: "Others",
        type: "folder",
        children: [
          {
            id: "m2-oth1",
            name: "TERI Indian Electricity Demand to 2030.pdf",
            type: "file",
            fileType: "pdf",
            filePath: "/resources/demand-flexibility-training-model/module-2/Reference Material/Others/Analysing and Projecting Indian Electricity Demand to 2030 by TERI.pdf"
          },
          {
            id: "m2-oth2",
            name: "PRAYAS Residential Consumption India.pdf",
            type: "file",
            fileType: "pdf",
            filePath: "/resources/demand-flexibility-training-model/module-2/Reference Material/Others/Residential electricity consumption in India by PRAYAS.pdf"
          }
        ]
      }
    ]
  },
  {
    id: "m3",
    name: "Module 3: DF Modelling",
    type: "folder",
    children: [
      {
        id: "m3-pptx",
        name: "MP Ensystems - Demand flexibility model 30-04-2025.pptx",
        type: "file",
        fileType: "pptx",
        filePath: "/resources/demand-flexibility-training-model/module-3/MP Ensystems - Demand flexibility model 30-04-2025.pptx",
        previewPath: "/resources/demand-flexibility-training-model/module-3/MP Ensystems - Demand flexibility model 30-04-2025.pdf"
      },
      {
        id: "m3-pdf",
        name: "Demand Flexibility Model Manual.pdf",
        type: "file",
        fileType: "pdf",
        filePath: "/resources/demand-flexibility-training-model/module-3/Demand Flexibility Model Manual 27-04-2025.pdf"
      },
      {
        id: "m3-ref1",
        name: "Input Data",
        type: "folder",
        children: [
          {
            id: "m3-csv1",
            name: "demand.csv",
            type: "file",
            fileType: "csv",
            filePath: "/resources/demand-flexibility-training-model/module-3/Input Data/demand.csv"
          },
          {
            id: "m3-xls1",
            name: "Generation_stack.xlsx",
            type: "file",
            fileType: "xlsx",
            filePath: "/resources/demand-flexibility-training-model/module-3/Input Data/Generation_stack.xlsx"
          },
          {
            id: "m3-csv2",
            name: "Historical weather data - Plant 1.csv",
            type: "file",
            fileType: "csv",
            filePath: "/resources/demand-flexibility-training-model/module-3/Input Data/Historical weather data-1_Plant.csv"
          },
          {
            id: "m3-csv3",
            name: "Historical weather data - Plant 2.csv",
            type: "file",
            fileType: "csv",
            filePath: "/resources/demand-flexibility-training-model/module-3/Input Data/Historical weather data-2_Plant.csv"
          },
          {
            id: "m3-csv4",
            name: "Info_Solar.csv",
            type: "file",
            fileType: "csv",
            filePath: "/resources/demand-flexibility-training-model/module-3/Input Data/Info_Solar.csv"
          },
          {
            id: "m3-csv5",
            name: "Info_Wind.csv",
            type: "file",
            fileType: "csv",
            filePath: "/resources/demand-flexibility-training-model/module-3/Input Data/Info_Wind.csv"
          },
          {
            id: "m3-csv6",
            name: "input_parameters.csv",
            type: "file",
            fileType: "csv",
            filePath: "/resources/demand-flexibility-training-model/module-3/Input Data/input_parameters.csv"
          },
          {
            id: "m3-xls2",
            name: "market_rate.xlsx",
            type: "file",
            fileType: "xlsx",
            filePath: "/resources/demand-flexibility-training-model/module-3/Input Data/market_rate.xlsx"
          },
          {
            id: "m3-xls3",
            name: "RE.xlsx",
            type: "file",
            fileType: "xlsx",
            filePath: "/resources/demand-flexibility-training-model/module-3/Input Data/RE.xlsx"
          },
          {
            id: "m3-csv7",
            name: "residential_1.csv",
            type: "file",
            fileType: "csv",
            filePath: "/resources/demand-flexibility-training-model/module-3/Input Data/residential_1.csv"
          },
          {
            id: "m3-csv8",
            name: "tariff.csv",
            type: "file",
            fileType: "csv",
            filePath: "/resources/demand-flexibility-training-model/module-3/Input Data/tariff.csv"
          },
          {
            id: "m3-ref1-sub",
            name: "meter data",
            type: "folder",
            children: [
              {
                id: "m3-nf-csv1",
                name: "commercial1.csv",
                type: "file",
                fileType: "csv",
                filePath: "/resources/demand-flexibility-training-model/module-3/Input Data/meter data/commercial1.csv"
              },
              {
                id: "m3-md-csv1",
                name: "hospital.csv",
                type: "file",
                fileType: "csv",
                filePath: "/resources/demand-flexibility-training-model/module-3/Input Data/meter data/hospital.csv"
              },
              {
                id: "m3-nf-csv2",
                name: "hospital1.csv",
                type: "file",
                fileType: "csv",
                filePath: "/resources/demand-flexibility-training-model/module-3/Input Data/meter data/hospital1.csv"
              },
              {
                id: "m3-md-csv2",
                name: "pww.csv",
                type: "file",
                fileType: "csv",
                filePath: "/resources/demand-flexibility-training-model/module-3/Input Data/meter data/pww.csv"
              },
              {
                id: "m3-md-csv3",
                name: "residential.csv",
                type: "file",
                fileType: "csv",
                filePath: "/resources/demand-flexibility-training-model/module-3/Input Data/meter data/residential.csv"
              }
            ]
          }
        ]
      }
    ]
  },
  {
    id: "m4",
    name: "Module 4: Program Designs & CBA",
    type: "folder",
    children: [
      {
        id: "m4-pptx1",
        name: "5 CEA and M&V Delhi DF training.pptx",
        type: "file",
        fileType: "pptx",
        filePath: "/resources/demand-flexibility-training-model/module-4/5 CEA and M&V Delhi DF training.pptx",
        previewPath: "/resources/demand-flexibility-training-model/module-4/5 CEA and M&V Delhi DF training.pdf"
      },
      {
        id: "m4-docx",
        name: "Manual-Training certification course_Module-IV.docx",
        type: "file",
        fileType: "docx",
        filePath: "/resources/demand-flexibility-training-model/module-4/Manual-Training certification course_Module-IV.docx",
        previewPath: "/resources/demand-flexibility-training-model/module-4/Manual-Training certification course_Module-IV.pdf"
      },
      {
        id: "m4-ref1",
        name: "Excel sheet",
        type: "folder",
        children: [
          {
            id: "m4-ex1",
            name: "DR program CBA.xlsx",
            type: "file",
            fileType: "xlsx",
            filePath: "/resources/demand-flexibility-training-model/module-4/Excel sheet/DR program CBA.xlsx"
          }
        ]
      },
      {
        id: "m4-ref2",
        name: "Reference Material",
        type: "folder",
        children: [
          {
            id: "m4-rm-xls",
            name: "Benefit-Cost calculations.xls",
            type: "file",
            fileType: "xls",
            filePath: "/resources/demand-flexibility-training-model/module-4/Reference Material/BENEFI~1.XLS"
          },
          {
            id: "m4-rm-pptx",
            name: "DSM Tests Measurement and Verification.pptx",
            type: "file",
            fileType: "pptx",
            filePath: "/resources/demand-flexibility-training-model/module-4/Reference Material/DSM Tests_Measurement an Verification.pptx",
            previewPath: "/resources/demand-flexibility-training-model/module-4/Reference Material/DSM Tests_Measurement an Verification.pdf"
          },
          {
            id: "m4-rm-pdf1",
            name: "SOP Handbook.pdf",
            type: "file",
            fileType: "pdf",
            filePath: "/resources/demand-flexibility-training-model/module-4/Reference Material/SOP-Handbook.pdf"
          },
          {
            id: "m4-rm-pdf2",
            name: "Understanding CBA.pdf",
            type: "file",
            fileType: "pdf",
            filePath: "/resources/demand-flexibility-training-model/module-4/Reference Material/UNDERS~1.PDF"
          },
          {
            id: "m4-rm-pdf3",
            name: "California CBA Guidelines.pdf",
            type: "file",
            fileType: "pdf",
            filePath: "/resources/demand-flexibility-training-model/module-4/Reference Material/MISCON~1.PDF"
          }
        ]
      }
    ]
  }
];

export default function DemandFlexibilityTrainingModelScreen() {
  const [selectedModuleId, setSelectedModuleId] = useState<number | null>(null);
  const [activeFile, setActiveFile] = useState<{ id: string; name: string; fileType: string; filePath?: string; previewPath?: string; moduleId: number } | null>(null);
  const [expandedFolderIds, setExpandedFolderIds] = useState<Record<string, boolean>>({});
  const [selectedSlideIndex, setSelectedSlideIndex] = useState<number>(0);
  const [zoomLevel, setZoomLevel] = useState<number>(100);

  // Enter the document explorer view from main modules grid
  const handleEnterModuleExplorer = (moduleId: number) => {
    setSelectedModuleId(moduleId);
    setSelectedSlideIndex(0);
    setZoomLevel(100);

    // Set default file view to pptx of that module
    const defaultPptxNames: Record<number, { name: string; path: string }> = {
      1: {
        name: "Module-I_Manual Training Course.pptx",
        path: "/resources/demand-flexibility-training-model/module-1/Module-I_Manual Training Course.pptx"
      },
      2: {
        name: "Introduction to Load Research Implementation.pptx",
        path: "/resources/demand-flexibility-training-model/module-2/Introduction to Load Research Implementation.pptx"
      },
      3: {
        name: "MP Ensystems - Demand flexibility model 30-04-2025.pptx",
        path: "/resources/demand-flexibility-training-model/module-3/MP Ensystems - Demand flexibility model 30-04-2025.pptx"
      },
      4: {
        name: "5 CEA and M&V Delhi DF training.pptx",
        path: "/resources/demand-flexibility-training-model/module-4/5 CEA and M&V Delhi DF training.pptx"
      }
    };

    const defaultPptx = defaultPptxNames[moduleId] || {
      name: "Slide deck.pptx",
      path: ""
    };

    setActiveFile({
      id: `m${moduleId}-pptx`,
      name: defaultPptx.name,
      fileType: "pptx",
      filePath: defaultPptx.path,
      previewPath: defaultPptx.path ? defaultPptx.path.replace(".pptx", ".pdf") : undefined,
      moduleId: moduleId
    });

    // Expand the selected module folder by default in tree
    setExpandedFolderIds({
      [`m${moduleId}`]: true
    });
  };

  const handleZoomIn = () => {
    setZoomLevel((prev) => Math.min(prev + 10, 150));
  };

  const handleZoomOut = () => {
    setZoomLevel((prev) => Math.max(prev - 10, 50));
  };

  const collapseNodeAndDescendants = (nextState: Record<string, boolean>, targetNode: ExplorerNode) => {
    nextState[targetNode.id] = false;
    const traverse = (n: ExplorerNode) => {
      if (n.children) {
        n.children.forEach((child) => {
          if (child.type === "folder") {
            nextState[child.id] = false;
            traverse(child);
          }
        });
      }
    };
    traverse(targetNode);
  };

  // Recursive rendering of the File Tree on the Left Side
  const renderFileTree = (nodes: ExplorerNode[], depth = 0) => {
    return nodes.map((node) => {
      const isFolder = node.type === "folder";
      const isExpanded = expandedFolderIds[node.id];
      const isActive = activeFile?.id === node.id;
      const isTopLevelModule = ["m1", "m2", "m3", "m4"].includes(node.id);

      const handleToggle = () => {
        if (isFolder) {
          const wasExpanded = !!expandedFolderIds[node.id];

          if (isTopLevelModule && !wasExpanded) {
            setExpandedFolderIds((prev) => {
              const nextState = { ...prev };
              // Close all other top level modules and their descendants
              fileTreeData.forEach((topNode) => {
                if (topNode.id !== node.id) {
                  collapseNodeAndDescendants(nextState, topNode);
                }
              });
              nextState[node.id] = true;
              return nextState;
            });
          } else {
            setExpandedFolderIds((prev) => {
              const nextState = { ...prev };
              const willExpand = !prev[node.id];
              if (!willExpand) {
                collapseNodeAndDescendants(nextState, node);
              } else {
                nextState[node.id] = true;
              }
              return nextState;
            });
          }
        }
      };

      const handleSelectFile = () => {
        if (!isFolder && node.fileType) {
          // Parse out the module number (e.g. from "m1-docx" or "m1-ferc1" -> 1)
          const parsedModId = parseInt(node.id.split("-")[0].replace("m", "")) || 1;
          setSelectedModuleId(parsedModId);
          setSelectedSlideIndex(0);

          setActiveFile({
            id: node.id,
            name: node.name,
            fileType: node.fileType,
            filePath: node.filePath,
            previewPath: node.previewPath,
            moduleId: parsedModId,
          });
        }
      };

      const handleDownloadFolderZip = (e: React.MouseEvent) => {
        e.stopPropagation();
        const zipUrls: Record<string, string> = {
          "m1": "/resources/demand-flexibility-training-model/module-1.zip",
          "m2": "/resources/demand-flexibility-training-model/module-2.zip",
          "m3": "/resources/demand-flexibility-training-model/module-3.zip",
          "m4": "/resources/demand-flexibility-training-model/module-4.zip",
        };
        const zipUrl = zipUrls[node.id];
        if (zipUrl) {
          triggerFileDownload(zipUrl, `${node.name}.zip`);
        } else {
          const folderNameClean = node.name.replace(/[:&/\\*?|<>\s]+/g, "_");
          triggerFileDownload("", `${folderNameClean}.zip`);
        }
      };

      const handleDownloadFile = (e: React.MouseEvent) => {
        e.stopPropagation();
        triggerFileDownload(node.filePath || "", node.name);
      };

      return (
        <div key={node.id} className="flex flex-col">
          {/* Tree Node Row */}
          <div
            onClick={isFolder ? handleToggle : handleSelectFile}
            className={`flex items-center justify-between py-2 px-2.5 rounded-lg select-none transition-all cursor-pointer group ${isTopLevelModule
              ? isExpanded
                ? "bg-[#e6f4ff] text-[#003350] border border-[#1890FF]/20 mb-1.5 shadow-sm"
                : "bg-white text-[#003350] border border-[#f0f0f0] hover:bg-gray-50 mb-1.5"
              : isFolder
                ? isExpanded
                  ? "bg-[#e6f4ff] text-[#003350] border border-[#1890FF]/10 mb-1"
                  : "text-cardHeading hover:bg-gray-100 border border-transparent"
                : "border border-transparent"
              } ${isActive
                ? "bg-[#e6f4ff] text-[#003350] border-l-2 border-[#1890FF] font-semibold"
                : isFolder ? "" : "text-cardText hover:bg-gray-50 hover:text-cardHeading"
              }`}
            style={{ paddingLeft: `${12 + depth * 16}px` }}
          >
            {/* Left side: Icon + Name */}
            <div className="flex items-center gap-2 min-w-0 flex-1">
              {isFolder ? (
                <>
                  <span className={`${isTopLevelModule ? "text-xl text-[#ffc107]" : "text-[#f1c40f] text-base"} shrink-0`}>
                    {isExpanded ? <FaFolderOpen /> : <FaFolder />}
                  </span>
                  <span
                    className={`${isTopLevelModule ? "text-sm font-semibold" : "text-xsmall font-medium"} font-poppins tracking-tight truncate select-none`}
                    title={node.name}
                  >
                    {node.name}
                  </span>
                </>
              ) : (
                <>
                  {/* Custom badges for docx, pptx, pdf, xlsx, csv, etc */}
                  <span
                    className={`text-[7px] font-sans font-bold px-1 py-0.5 rounded text-white shrink-0 uppercase tracking-wider leading-none select-none`}
                    style={{
                      backgroundColor:
                        node.fileType === "docx" ? "#1890FF" :
                          node.fileType === "pptx" ? "#ff4d4f" :
                            node.fileType === "pdf" ? "#e03e2d" :
                              ["xlsx", "xls", "csv"].includes(node.fileType || "") ? "#27ae60" : "#7f8c8d"
                    }}
                  >
                    {node.fileType}
                  </span>
                  <span
                    className="text-xsmall font-poppins truncate leading-tight select-none"
                    title={node.name}
                  >
                    {node.name}
                  </span>
                </>
              )}
            </div>

            {/* Right side: Download Icon Button */}
            {(!isFolder || depth === 0) && (
              <button
                onClick={isFolder ? handleDownloadFolderZip : handleDownloadFile}
                className="text-gray-400 hover:text-[#003350] transition-colors p-1 cursor-pointer shrink-0 opacity-45 group-hover:opacity-100 focus:outline-none"
                title={isFolder ? `Download "${node.name}" folder contents as ZIP` : `Download "${node.name}"`}
              >
                <LuDownload className="w-3.5 h-3.5" />
              </button>
            )}
          </div>

          {/* Child Node Rows */}
          {isFolder && isExpanded && node.children && (
            <div className="flex flex-col">
              {renderFileTree(node.children, depth + 1)}
            </div>
          )}
        </div>
      );
    });
  };

  // Render Grid View of 4 Modules
  if (selectedModuleId === null || !activeFile) {
    return (
      <div className="pt-20 bg-mapGray box-border min-h-screen">
        <Banner
          title="Demand Flexibility Training Module"
          isRounded
          backgroundImg="/static/images/india.webp"
        />
        <div className="py-[60px] mobileMax:py-10">
          <div className="mini-container">
            <div className="flex flex-col laptop:flex-row gap-8 items-start">
              {/* Left section - 4 modules grid */}
              <div className="w-full laptop:w-[72%]">
                <div className="grid grid-cols-1 tab:grid-cols-2 gap-6">
                  {modulesDataList.map((module) => (
                    <div
                      key={module.id}
                      onClick={() => handleEnterModuleExplorer(module.id)}
                      className="bg-white rounded-2xl overflow-hidden border border-grayBorder hover:shadow-lg transition-all duration-300 flex flex-col h-full card-shadow cursor-pointer"
                    >
                      {/* Top Gradient Header */}
                      <div
                        className="h-44 relative flex items-center justify-center"
                        style={{ background: module.gradient }}
                      >
                        {/* Module Number Badge */}
                        <span className="absolute top-4 left-4 bg-[#0a1e2c]/65 text-white font-poppins text-xs font-semibold px-3 py-1.5 rounded-[4px] uppercase tracking-wider">
                          {module.moduleNum}
                        </span>
                        {/* File Types Badge */}
                        <span className="absolute bottom-4 right-4 text-white font-poppins text-[10px] font-bold tracking-wider opacity-90">
                          {module.fileTypes}
                        </span>
                      </div>

                      {/* Bottom Content Section */}
                      <div className="p-6 flex flex-col flex-grow">
                        <h3 className="text-[#003350] font-poppins font-bold text-medium leading-snug mb-3 min-h-[56px]">
                          {module.title}
                        </h3>
                        <p className="text-cardText font-poppins text-xsmall leading-relaxed mb-6 flex-grow">
                          {module.description}
                        </p>
                        <span
                          className="text-[#1890FF] font-poppins text-xsmall font-semibold hover:text-[#48DBB2] transition-colors flex items-center gap-1.5 mt-auto"
                        >
                          View module &rarr;
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Right section - Share Sidebar */}
              <div className="w-full laptop:w-[28%] laptop:sticky laptop:top-24">
                <div className="bg-white rounded-2xl p-6 border border-grayBorder shadow-sm flex flex-col gap-6 card-shadow">
                  {/* Share Section */}
                  <div>
                    <span className="text-cardHeading font-poppins font-bold text-small mb-3 block">
                      Share:
                    </span>
                    <div className="flex items-center gap-3">
                      <a
                        href="#"
                        className="bg-[#7a8b99] text-white rounded-full w-9 h-9 flex items-center justify-center hover:opacity-90 transition-opacity cursor-pointer text-small"
                        title="Share via Email"
                      >
                        <FaEnvelope />
                      </a>
                      <a
                        href="#"
                        className="bg-[#3b5998] text-white rounded-full w-9 h-9 flex items-center justify-center hover:opacity-90 transition-opacity cursor-pointer text-small"
                        title="Share on Facebook"
                      >
                        <FaFacebookF />
                      </a>
                      <a
                        href="#"
                        className="bg-[#1da1f2] text-white rounded-full w-9 h-9 flex items-center justify-center hover:opacity-90 transition-opacity cursor-pointer text-small"
                        title="Share on Twitter"
                      >
                        <FaXTwitter />
                      </a>
                      <a
                        href="#"
                        className="bg-gradient-to-tr from-[#f9ce34] via-[#ee2a7b] to-[#6228d7] text-white rounded-full w-9 h-9 flex items-center justify-center hover:opacity-90 transition-opacity cursor-pointer text-small"
                        title="Share on Instagram"
                      >
                        <FaInstagram />
                      </a>
                      <a
                        href="#"
                        className="bg-[#25d366] text-white rounded-full w-9 h-9 flex items-center justify-center hover:opacity-90 transition-opacity cursor-pointer text-small"
                        title="Share on WhatsApp"
                      >
                        <FaWhatsapp />
                      </a>
                    </div>
                  </div>

                  {/* Learn More Button */}
                  <a
                    href="#"
                    className="bg-gradient-to-r from-[#48dbb2] to-[#003350] text-white font-poppins font-semibold text-small py-3 px-6 rounded-full hover:opacity-95 transition-opacity text-center flex items-center justify-center gap-1 cursor-pointer w-full shadow-sm"
                  >
                    Learn more &rsaquo;
                  </a>

                  {/* Divider */}
                  <div className="border-t border-grayBorder my-1" />

                  {/* Description Text */}
                  <p className="text-xs text-cardText font-poppins text-center leading-relaxed">
                    Each module includes a training manual, slide deck, and curated
                    regulatory reference material.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // document details view for the selected module file
  const activeModuleData = modulesDetailedData[activeFile.moduleId];
  const totalPages = activeModuleData.manualPages.length;
  const totalSlides = activeModuleData.slides.length;

  return (
    <div className="pt-20 bg-mapGray box-border min-h-screen">
      <Banner
        title="Demand Flexibility Training Module"
        isRounded
        backgroundImg="/static/images/india.webp"
      />
      <div className="py-[60px] mobileMax:py-10">
        <div className="mini-container">
          {/* Back Button controls */}
          <div className="flex justify-between items-center mb-6">
            <button
              onClick={() => setSelectedModuleId(null)}
              className="cursor-pointer flex items-center gap-2 text-[#003350] hover:text-[#1890FF] font-poppins text-small font-semibold transition-colors focus:outline-none"
            >
              <FaArrowLeft />
              <span>Back to Modules</span>
            </button>
          </div>

          <div className="flex flex-col laptop:flex-row gap-6 items-start">
            {/* Left section - Reference Material File Tree Explorer */}
            <div className="w-full laptop:w-[25%] laptop:shrink-0 laptop:sticky laptop:top-24">
              <div className="bg-white rounded-2xl p-6 border border-grayBorder shadow-sm flex flex-col gap-4 commonBoxShadow">
                <span className="text-[#003350] font-poppins font-bold text-small uppercase tracking-wider block border-b border-gray-100 pb-2">
                  Reference Material
                </span>


                <div className="flex flex-col gap-1 mt-2 max-h-[500px] overflow-y-auto no-scrollbar">
                  {renderFileTree(fileTreeData)}
                </div>
              </div>
            </div>

            {/* Right section - High fidelity Document Viewer */}
            <div className="w-full laptop:flex-1 flex flex-col bg-[#323336] rounded-2xl overflow-hidden shadow-lg border border-[#444547]">
              {/* PDF style toolbar */}
              <div className="bg-[#202124] h-12 px-4 flex items-center justify-between text-white border-b border-[#3b3c3f]">
                {/* Left side info: Name only */}
                <div className="flex items-center gap-4">
                  <span
                    className="text-xs font-poppins text-gray-200 font-semibold truncate max-w-[350px] md:max-w-[700px]"
                    title={activeFile.name}
                  >
                    {activeFile.name}
                  </span>
                </div>

                {/* Right utility option: Download File only */}
                <div className="flex items-center gap-4 shrink-0">
                  <button
                    className="text-gray-400 hover:text-white transition-colors cursor-pointer"
                    title="Download File"
                    onClick={() => triggerFileDownload(activeFile.filePath || "", activeFile.name)}
                  >
                    <LuDownload className="w-5 h-5" />
                  </button>
                </div>
              </div>

              {/* Viewer Area */}
              <div className="p-6 h-[600px] overflow-auto flex justify-center bg-[#525659] relative">
                {activeFile.previewPath || (activeFile.fileType === "pdf" && activeFile.filePath) ? (
                  <div className="w-full h-full bg-[#525659] p-0">
                    <iframe
                      src={encodeFilePath(activeFile.previewPath || activeFile.filePath)}
                      className="w-full h-full border-none bg-white"
                      title={activeFile.name}
                      loading="lazy"
                    />
                  </div>
                ) : activeFile.fileType === "docx" ? (
                  /* Training manual DOCX/PDF Layout (Vertical scrolling pages) */
                  <div
                    className="flex flex-col gap-6 items-center transition-all duration-300"
                    style={{ width: `${100 * (zoomLevel / 100)}%`, maxWidth: "800px" }}
                  >
                    {activeModuleData.manualPages.map((page, idx) => (
                      <div
                        key={idx}
                        className="bg-white text-black shadow-2xl p-12 w-full aspect-[1/1.414] flex flex-col justify-between relative border border-gray-300 font-serif"
                      >
                        <div>
                          {/* Page Header */}
                          <div className="border-b border-gray-200 pb-2 mb-6 flex justify-between items-center text-[10px] uppercase text-gray-500 tracking-wider font-sans">
                            <span>{page.header}</span>
                            <span>Module {activeFile.moduleId}</span>
                          </div>

                          {/* Content */}
                          {page.heading && (
                            <h2 className="text-[#003350] font-sans font-bold text-medium tracking-tight mb-4 leading-snug">
                              {page.heading}
                            </h2>
                          )}

                          {page.paragraphs.map((p, pIdx) => (
                            <p key={pIdx} className="text-gray-800 text-[13px] leading-relaxed mb-4 text-justify font-sans">
                              {p}
                            </p>
                          ))}
                        </div>

                        {/* Page Footer */}
                        <div className="border-t border-gray-100 pt-3 text-right text-[10px] text-gray-400 font-sans">
                          Page {idx + 1}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : activeFile.fileType === "pptx" ? (
                  /* Slide deck PPTX Layout (Thumbnails sidebar + Active slide viewport) */
                  <div className="flex w-full h-full gap-4 items-stretch justify-start">
                    {/* Left Thumbnails strip */}
                    <div className="w-44 shrink-0 bg-[#2f3136] rounded border border-[#3b3c3f] p-3 flex flex-col gap-4 overflow-y-auto no-scrollbar">
                      {activeModuleData.slides.map((slide, idx) => (
                        <div
                          key={slide.slideNum}
                          onClick={() => setSelectedSlideIndex(idx)}
                          className={`cursor-pointer group flex flex-col gap-1 items-center transition-all duration-200`}
                        >
                          <div
                            className={`w-full aspect-[16/9] rounded overflow-hidden shadow border transition-all duration-300 relative ${selectedSlideIndex === idx
                              ? "border-[#48DBB2] ring-2 ring-[#48DBB2]/30 scale-[1.02]"
                              : "border-gray-600 hover:border-gray-400"
                              }`}
                            style={{
                              background: activeModuleData.gradient,
                              backgroundSize: "cover"
                            }}
                          >
                            {/* Slide Title Mini-preview */}
                            <div className="p-1 flex flex-col justify-center items-center h-full text-center">
                              <span className="text-[6px] leading-[8px] font-sans text-white font-bold opacity-80 line-clamp-2 px-1">
                                {slide.title}
                              </span>
                            </div>
                          </div>
                          <span className={`text-[10px] font-semibold ${selectedSlideIndex === idx ? "text-[#48DBB2]" : "text-gray-400"}`}>
                            {idx + 1}
                          </span>
                        </div>
                      ))}
                    </div>

                    {/* Active Slide Canvas */}
                    <div className="flex-grow flex items-center justify-center bg-[#28292c] rounded p-4 relative overflow-hidden">
                      <div
                        className="aspect-[16/9] bg-white text-black shadow-2xl p-8 relative flex flex-col justify-between font-sans transition-all duration-300 w-full"
                        style={{
                          backgroundImage: `linear-gradient(rgba(255,255,255,0.96), rgba(255,255,255,0.96)), ${activeModuleData.gradient}`,
                          backgroundSize: "cover",
                          width: `${100 * (zoomLevel / 100)}%`
                        }}
                      >
                        {/* Slide Top Logo Section */}
                        <div className="flex justify-between items-center border-b border-gray-100 pb-3">
                          <div className="flex items-center gap-1.5">
                            <div className="w-4 h-4 rounded-full bg-[#11375c] flex items-center justify-center text-white text-[8px] font-bold">ME</div>
                            <span className="text-[10px] font-bold text-[#003350] uppercase tracking-wider font-poppins">Mission Efficiency</span>
                          </div>
                          <span className="text-[9px] font-bold text-gray-500 uppercase tracking-widest">MP ENSYSTEMS</span>
                        </div>

                        {/* Slide Core Content */}
                        <div className="my-auto py-2">
                          <h2 className="text-[#003350] font-bold text-medium leading-snug mb-4 font-poppins">
                            {activeModuleData.slides[selectedSlideIndex].title}
                          </h2>

                          {activeModuleData.slides[selectedSlideIndex].subtitle && (
                            <p className="text-gray-600 text-xsmall italic font-medium">
                              {activeModuleData.slides[selectedSlideIndex].subtitle}
                            </p>
                          )}

                          {activeModuleData.slides[selectedSlideIndex].date && (
                            <p className="text-gray-400 text-xs mt-2 font-mono">
                              {activeModuleData.slides[selectedSlideIndex].date}
                            </p>
                          )}

                          {activeModuleData.slides[selectedSlideIndex].bullets && (
                            <ul className="list-disc pl-6 flex flex-col gap-2 mt-2">
                              {activeModuleData.slides[selectedSlideIndex].bullets?.map((bullet, bIdx) => (
                                <li key={bIdx} className="text-gray-800 text-xsmall leading-relaxed">
                                  {bullet}
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>

                        {/* Slide Footer */}
                        <div className="flex justify-between items-center text-[9px] text-gray-400 border-t border-gray-100 pt-2">
                          <span>Module {activeFile.moduleId} Training Deck</span>
                          <span className="font-semibold">Slide {selectedSlideIndex + 1} of {totalSlides}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : ["xlsx", "xls", "csv", "ppt"].includes(activeFile.fileType) ? (
                  <div className="flex flex-col items-center justify-center text-white h-full w-full max-w-md mx-auto text-center gap-6 p-8">
                    <div className="w-20 h-20 rounded-2xl bg-green-600/20 border border-green-500/30 flex items-center justify-center text-green-400 text-4xl shadow-lg shadow-green-500/5">
                      <IoDocumentTextOutline className="w-12 h-12" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-white font-poppins mb-2">{activeFile.name}</h3>
                      <p className="text-xs text-gray-400 font-poppins leading-relaxed">
                        This file contains data, code or reference materials for Module {activeFile.moduleId}. Click the button below to download and open the file.
                      </p>
                    </div>
                    <button
                      onClick={() => triggerFileDownload(activeFile.filePath || "", activeFile.name)}
                      className="bg-green-600 hover:bg-green-700 text-white font-poppins text-xs font-semibold py-3 px-6 rounded-full transition-colors flex items-center gap-2 cursor-pointer shadow-md"
                    >
                      <LuDownload className="w-4 h-4" />
                      Download File
                    </button>
                  </div>
                ) : activeFile.fileType === "pdf" && activeFile.filePath ? (
                  <div className="w-full h-full bg-[#525659] p-0">
                    <iframe
                      src={encodeFilePath(activeFile.filePath)}
                      className="w-full h-full border-none bg-white"
                      title={activeFile.name}
                      loading="lazy"
                    />
                  </div>
                ) : (
                  /* Reference Document PDF Layout (Vertical pages) */
                  <div
                    className="flex flex-col gap-6 items-center transition-all duration-300"
                    style={{ width: `${100 * (zoomLevel / 100)}%`, maxWidth: "800px" }}
                  >
                    {pdfMockData[activeFile.id] ? (
                      <div className="bg-white text-black shadow-2xl p-12 w-full aspect-[1/1.414] flex flex-col justify-between relative border border-gray-300 font-sans">
                        <div>
                          {/* Header */}
                          <div className="border-b border-gray-200 pb-2 mb-6 flex justify-between items-center text-[10px] uppercase text-[#e03e2d] tracking-wider font-semibold">
                            <span>Reference Document (PDF)</span>
                            <span>{activeFile.name.split(" ")[0]}</span>
                          </div>

                          {/* Titles */}
                          <h2 className="text-[#003350] font-poppins font-bold text-medium tracking-tight mb-2 leading-snug">
                            {pdfMockData[activeFile.id].title}
                          </h2>
                          <p className="text-gray-500 text-xs font-semibold mb-6 italic leading-none">
                            {pdfMockData[activeFile.id].subtitle}
                          </p>

                          {/* Paragraphs */}
                          {pdfMockData[activeFile.id].paragraphs.map((para, pIdx) => (
                            <p key={pIdx} className="text-gray-800 text-xsmall leading-relaxed mb-4 text-justify">
                              {para}
                            </p>
                          ))}
                        </div>

                        {/* Footer */}
                        <div className="border-t border-gray-100 pt-3 text-right text-[10px] text-gray-400">
                          Reference Material Page 1 of 1
                        </div>
                      </div>
                    ) : (
                      <div className="bg-white shadow p-12 rounded text-center w-full">
                        <p className="text-gray-500">Document viewer content loading...</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
